import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useStrategyStore } from '@/stores/strategy';
import type { MarketingPlan as MarketingPlanType } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AIInput } from '@/components/ui/ai-input';
import { AITextarea } from '@/components/ui/ai-textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  type MarketingPlan,
  type MarketingPhase,
  type MarketingActivity,
  type ActivityType,
  type ActivityStatus,
  type PriorityLevel,
  type PlanGoal,
  type PlanGoalMeasurement,
  type PlanStatus,
  type PhaseStatus,
  type MarketingStrategy,
} from '@/types';
import type { ApiResponse, PaginatedResponse } from '@/types';
import { cn, formatCurrency, formatDate, statusColors } from '@/lib/utils';
import { activityTypeBg, activityTypeHex } from '@/lib/tag-palette';
import { api } from '@/lib/api';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { InfoTooltip } from '@/components/help/InfoTooltip';
import { SectionHeadingWithHint } from '@/components/help/SectionHeadingWithHint';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import ActivityDetailDrawer from '@/pages/activities/ActivityDetailDrawer';
import {
  Pencil,
  Download,
  Copy,
  Trash2,
  ChevronDown,
  ChevronRight,
  Target,
  Calendar,
  CalendarDays,
  MessageSquare,
  Send,
  Loader2,
  ImageIcon,
  Lightbulb,
  Wallet2,
  Mail,
  FileText,
  Search,
  Megaphone,
  Video,
  Printer,
  Share2,
  TrendingUp,
  Users,
  Plus,
  X,
  Eraser,
  Undo2,
  Save,
  BarChart2,
  Link2,
} from 'lucide-react';

const CURRENT_YEAR = new Date().getFullYear();
const PLAN_STATUS_LABELS: Record<PlanStatus, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  completed: 'Abgeschlossen',
  archived: 'Archiviert',
};
const PHASE_STATUS_LABELS: Record<PhaseStatus, string> = {
  pending: 'Ausstehend',
  in_progress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
};

/** Endpunkt der Fortschrittslinie: Mitte der letzten zusammenhängenden abgeschlossenen Phase bzw. erste „In Bearbeitung“. */
function getPhaseTimelineProgressMeta(phasesSorted: MarketingPhase[]): {
  widthPct: number;
  endStatus: PhaseStatus | null;
} {
  const n = phasesSorted.length;
  if (n === 0) return { widthPct: 0, endStatus: null };
  if (phasesSorted.every((p) => p.status === 'completed')) {
    return { widthPct: 100, endStatus: 'completed' };
  }

  let endIdx = -1;
  for (let i = 0; i < n; i++) {
    const phase = phasesSorted[i];
    if (!phase) break;
    const s = phase.status;
    if (s === 'completed') endIdx = i;
    else if (s === 'in_progress') {
      endIdx = i;
      break;
    } else break;
  }
  if (endIdx < 0) return { widthPct: 0, endStatus: null };

  const endPhase = phasesSorted[endIdx];
  if (!endPhase) return { widthPct: 0, endStatus: null };

  return {
    widthPct: ((endIdx + 0.5) / n) * 100,
    endStatus: endPhase.status,
  };
}

const ACTIVITY_STATUS_LABELS: Record<ActivityStatus, string> = {
  backlog: 'Backlog',
  todo: 'Offen',
  in_progress: 'In Arbeit',
  review: 'Review',
  done: 'Erledigt',
};

const ACTIVITY_TYPE_META: Record<ActivityType, { label: string; icon: React.ReactNode; bg: string; text: string }> = {
  google_ads:   { label: 'Google Ads',  icon: <TrendingUp className="h-3.5 w-3.5" />,   bg: activityTypeBg('google_ads'),   text: 'text-white' },
  linkedin_ads: { label: 'LinkedIn',    icon: <Users className="h-3.5 w-3.5" />,        bg: activityTypeBg('linkedin_ads'), text: 'text-white' },
  email:        { label: 'E-Mail',      icon: <Mail className="h-3.5 w-3.5" />,         bg: activityTypeBg('email'),        text: 'text-white' },
  content:      { label: 'Content',     icon: <FileText className="h-3.5 w-3.5" />,     bg: activityTypeBg('content'),      text: 'text-white' },
  seo:          { label: 'SEO',         icon: <Search className="h-3.5 w-3.5" />,       bg: activityTypeBg('seo'),          text: 'text-white' },
  campaign:     { label: 'Kampagne',    icon: <Megaphone className="h-3.5 w-3.5" />,    bg: activityTypeBg('campaign'),     text: 'text-white' },
  event:        { label: 'Event',       icon: <CalendarDays className="h-3.5 w-3.5" />, bg: activityTypeBg('event'),        text: 'text-white' },
  print:        { label: 'Print',       icon: <Printer className="h-3.5 w-3.5" />,      bg: activityTypeBg('print'),        text: 'text-white' },
  webinar:      { label: 'Webinar',     icon: <Video className="h-3.5 w-3.5" />,        bg: activityTypeBg('webinar'),      text: 'text-white' },
  social:       { label: 'Social',      icon: <Share2 className="h-3.5 w-3.5" />,       bg: activityTypeBg('social'),       text: 'text-white' },
  other:        { label: 'Sonstiges',   icon: <Megaphone className="h-3.5 w-3.5" />,    bg: activityTypeBg('other'),        text: 'text-primary-foreground' },
};

const ACTIVITY_TYPE_TIMELINE_COLOR: Record<ActivityType, string> = {
  google_ads: activityTypeHex('google_ads'), linkedin_ads: activityTypeHex('linkedin_ads'), email: activityTypeHex('email'),
  content: activityTypeHex('content'), seo: activityTypeHex('seo'), campaign: activityTypeHex('campaign'),
  event: activityTypeHex('event'), print: activityTypeHex('print'), webinar: activityTypeHex('webinar'),
  social: activityTypeHex('social'), other: activityTypeHex('other'),
};

const PRIORITY_BORDER: Record<PriorityLevel, string> = {
  critical: 'border-l-red-500',
  high:     'border-l-orange-500',
  medium:   'border-l-amber-400',
  low:      'border-l-slate-300 dark:border-l-slate-600',
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

interface PhaseGanttViewProps {
  phase: MarketingPhase & { activities?: MarketingActivity[] };
  onActivityClick: (id: string) => void;
}

function PhaseGanttView({ phase, onActivityClick }: PhaseGanttViewProps) {
  const activities = phase.activities ?? [];

  const pStart = phase.start_date ? new Date(phase.start_date).getTime() : Date.now();
  const pEnd   = phase.end_date   ? new Date(phase.end_date).getTime()   : pStart + 90 * 86_400_000;
  const pDur   = pEnd - pStart;

  // Percentage helpers — makes the chart always fill 100% of available width
  const toPct = (ms: number) => Math.max(0, Math.min(100, ((ms - pStart) / pDur) * 100));

  const today = Date.now();
  const showToday = today >= pStart && today <= pEnd;
  const todayPct  = toPct(today);

  // Month tick marks as percentages
  const monthTicks: { label: string; pct: number }[] = [];
  const d = new Date(pStart);
  d.setDate(1);
  if (new Date(pStart).getDate() > 1) d.setMonth(d.getMonth() + 1);
  while (d.getTime() <= pEnd) {
    monthTicks.push({ label: `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`, pct: toPct(d.getTime()) });
    d.setMonth(d.getMonth() + 1);
  }

  const getBar = (a: MarketingActivity): { leftPct: number; widthPct: number } | null => {
    if (!a.start_date) return null;
    const aS = new Date(a.start_date).getTime();
    const aE = a.end_date ? new Date(a.end_date).getTime() : aS + 86_400_000 * 3;
    return {
      leftPct:  toPct(aS),
      widthPct: Math.max(((aE - aS) / pDur) * 100, 1.5),
    };
  };

  if (activities.length === 0) {
    return <p className="text-sm text-muted">Keine Aktivitäten in dieser Phase.</p>;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex">
        {/* ── Left column: activity meta ── */}
        <div className="w-64 shrink-0 border-r border-border">
          {/* header */}
          <div className="flex h-8 items-center border-b border-border bg-background dark:bg-card-hover px-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">Aktivität</span>
          </div>
          {activities.map((a, idx) => {
            const meta = ACTIVITY_TYPE_META[a.type] ?? ACTIVITY_TYPE_META.other;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => onActivityClick(a.id)}
                className={cn(
                  'flex h-11 w-full items-center gap-2 px-3 text-left transition-colors',
                  'hover:bg-background dark:hover:bg-card-hover border-b border-border',
                  idx % 2 === 0 ? 'bg-card' : 'bg-background/50 dark:bg-card-hover/30',
                )}
              >
                <span className={cn('flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium', meta.bg, meta.text)}>
                  {meta.icon}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{a.title}</span>
                <Badge className={cn('shrink-0 text-[10px]', statusColors[a.status])}>
                  {ACTIVITY_STATUS_LABELS[a.status]}
                </Badge>
              </button>
            );
          })}
        </div>

        {/* ── Right column: percentage-based, always fills full container width ── */}
        <div className="relative flex-1 min-w-0">
          {/* Month header */}
          <div className="relative h-8 border-b border-border bg-background dark:bg-card-hover">
            {monthTicks.map((tick) => (
              <span
                key={tick.pct}
                className="absolute top-1.5 whitespace-nowrap text-[10px] font-semibold text-muted pl-1"
                style={{ left: `${tick.pct}%` }}
              >
                {tick.label}
              </span>
            ))}
            {monthTicks.map((tick) => (
              <div
                key={`line-${tick.pct}`}
                className="absolute top-0 bottom-0 w-px bg-card-hover"
                style={{ left: `${tick.pct}%` }}
              />
            ))}
          </div>

          {/* Today line */}
          {showToday && (
            <div
              className="absolute top-0 bottom-0 z-10 w-0.5 bg-danger pointer-events-none"
              style={{ left: `${todayPct}%` }}
            />
          )}

          {/* Activity rows */}
          {activities.map((a, idx) => {
            const bar = getBar(a);
            const color = ACTIVITY_TYPE_TIMELINE_COLOR[a.type] ?? '#94a3b8';
            const supplierName = (a as MarketingActivity & { suppliers?: { name: string } }).suppliers?.name ?? a.supplier?.name;
            return (
              <div
                key={a.id}
                className={cn(
                  'relative h-11 border-b border-border',
                  idx % 2 === 0 ? 'bg-card' : 'bg-background/50 dark:bg-card-hover/30',
                )}
              >
                {monthTicks.map((tick) => (
                  <div
                    key={`row-line-${tick.pct}`}
                    className="absolute top-0 bottom-0 w-px bg-card-hover"
                    style={{ left: `${tick.pct}%` }}
                  />
                ))}

                {bar && (
                  <button
                    type="button"
                    onClick={() => onActivityClick(a.id)}
                    title={`${a.title}${supplierName ? ` · ${supplierName}` : ''}\n${a.start_date ?? ''} – ${a.end_date ?? ''}`}
                    className="absolute top-2 h-7 rounded-md shadow-sm transition-all hover:brightness-110 hover:shadow-md focus:outline-none"
                    style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%`, backgroundColor: color }}
                  >
                    <span className="absolute inset-x-1.5 top-1 truncate text-left text-[10px] font-semibold text-white drop-shadow-sm">
                      {a.title}
                    </span>
                    {supplierName && (
                      <span className="absolute inset-x-1.5 bottom-0.5 truncate text-left text-[10px] text-white/75">
                        {supplierName}
                      </span>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
const MARKETING_PLAN_STOCK_IMAGE =
  'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80';

const getGoalTextValue = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

const VALID_PERIODS: PlanGoalMeasurement['period'][] = ['Q1', 'Q2', 'Q3', 'Q4', 'year'];

const normalizeMeasurements = (raw: unknown): PlanGoalMeasurement[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((m): PlanGoalMeasurement | null => {
      if (!m || typeof m !== 'object' || Array.isArray(m)) return null;
      const r = m as Record<string, unknown>;
      const period = VALID_PERIODS.includes(r.period as PlanGoalMeasurement['period'])
        ? (r.period as PlanGoalMeasurement['period'])
        : null;
      if (!period) return null;
      const rawValue = r.value;
      const value = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
      if (!Number.isFinite(value)) return null;
      return {
        period,
        value,
        date: typeof r.date === 'string' ? r.date : new Date().toISOString().substring(0, 10),
        ...(typeof r.notes === 'string' && r.notes ? { notes: r.notes } : {}),
      };
    })
    .filter((m): m is PlanGoalMeasurement => m !== null);
};

const normalizePlanGoals = (rawGoals: unknown): PlanGoal[] => {
  if (!Array.isArray(rawGoals)) return [];

  return rawGoals
    .map((rawGoal, index) => {
      if (typeof rawGoal === 'string') {
        const title = rawGoal.trim();
        if (!title) return null;
        return {
          id: `goal-${index + 1}`,
          title,
          target: '',
          metric: '',
        } satisfies PlanGoal;
      }

      if (!rawGoal || typeof rawGoal !== 'object') return null;

      const goalRecord = rawGoal as Record<string, unknown>;
      const id = getGoalTextValue(goalRecord.id) || `goal-${index + 1}`;
      const title =
        getGoalTextValue(goalRecord.title) ||
        getGoalTextValue(goalRecord.target) ||
        getGoalTextValue(goalRecord.metric) ||
        `Ziel ${index + 1}`;
      const target = getGoalTextValue(goalRecord.target);
      const metric = getGoalTextValue(goalRecord.metric);
      const current = getGoalTextValue(goalRecord.current);
      const measurements = normalizeMeasurements(goalRecord.measurements);

      return {
        id,
        title,
        target,
        metric,
        ...(current ? { current } : {}),
        ...(measurements.length > 0 ? { measurements } : {}),
      } satisfies PlanGoal;
    })
    .filter((goal): goal is PlanGoal => goal !== null);
};

export function MarketingPlanPage() {
  const confirmDialog = useConfirmDialog();
  const [year, setYear] = useState(CURRENT_YEAR);
  const [plan, setPlan] = useState<MarketingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const loadRequest = useRef(0);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState<MarketingPlan | null>(null);
  const [editingPhase, setEditingPhase] = useState<MarketingPhase | null>(null);
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const [copying, setCopying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [measureDialogOpen, setMeasureDialogOpen] = useState(false);
  const [measureGoalId, setMeasureGoalId] = useState<string | null>(null);
  const [measureForm, setMeasureForm] = useState<{
    period: PlanGoalMeasurement['period'];
    value: string;
    notes: string;
  }>({ period: 'Q1', value: '', notes: '' });
  const [savingMeasure, setSavingMeasure] = useState(false);
  const [lastPlanSnapshot, setLastPlanSnapshot] = useState<{
    id: string;
    title: string;
    description: string;
    status: PlanStatus;
    goals: PlanGoal[];
    total_budget: number;
    year: number;
  } | null>(null);

  const [planForm, setPlanForm] = useState({
    year: CURRENT_YEAR,
    title: '',
    description: '',
    status: 'draft' as PlanStatus,
    goals: [] as PlanGoal[],
    total_budget: '0',
  });
  const [phaseForm, setPhaseForm] = useState({
    name: '',
    description: '',
    status: 'pending' as PhaseStatus,
    start_date: '',
    end_date: '',
  });

  const loadPlan = useCallback(async () => {
    const request = ++loadRequest.current;
    setLoading(true);
    try {
      const res = await api.get<ApiResponse<MarketingPlan[]>>(
        `/marketing-plans?year=${year}`
      );
      const data = res.data;
      if (request !== loadRequest.current) return;
      if (data && Array.isArray(data) && data.length > 0) {
        const first = data[0];
        if (!first) {
          setPlan(null);
        } else {
          const planRes = await api.get<ApiResponse<MarketingPlan>>(
            `/marketing-plans/${first.id}`
          );
          const nextPlan = planRes?.data;
          if (request !== loadRequest.current) return;
          if (!nextPlan) {
            setPlan(null);
          } else {
            setPlan({
              ...nextPlan,
              goals: normalizePlanGoals(nextPlan.goals),
            });
          }
        }
      } else {
        setPlan(null);
      }
    } catch (error) {
      if (request !== loadRequest.current) return;
      setPlan(null);
      setActionError(error instanceof Error ? error.message : 'Plan konnte nicht geladen werden.');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    void loadPlan();
    return () => { loadRequest.current += 1; };
  }, [loadPlan]);

  const handleCopyPlan = async () => {
    if (!plan) return;
    setCopying(true);
    setActionError(null);
    try {
      const res = await api.post<ApiResponse<MarketingPlan>>(
        `/marketing-plans/${plan.id}/copy`
      );
      setYear(res.data?.year ?? plan.year + 1);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Plan konnte nicht kopiert werden.');
    } finally {
      setCopying(false);
    }
  };

  const togglePhase = (id: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const focusPhaseFromTimeline = (id: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    window.requestAnimationFrame(() => {
      document.getElementById(`marketing-phase-${id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  };

  const openPlanDialog = (p?: MarketingPlan) => {
    setActionError(null);
    if (p) {
      setEditingPlan(p);
      setPlanForm({
        year: p.year,
        title: p.title,
        description: p.description ?? p.description_md ?? '',
        status: p.status,
        goals: normalizePlanGoals(p.goals),
        total_budget: String(p.total_budget ?? 0),
      });
    } else {
      setEditingPlan(null);
      setPlanForm({
        year,
        title: `Marketing-Plan ${year}`,
        description: '',
        status: 'draft',
        goals: [],
        total_budget: '0',
      });
    }
    setPlanDialogOpen(true);
  };

  const savePlan = async () => {
    setActionError(null);
    const goals = normalizePlanGoals(planForm.goals);
    const totalBudget = Number(planForm.total_budget);
    if (!planForm.title.trim()) {
      setActionError('Bitte einen Titel eingeben.');
      return;
    }
    if (!Number.isFinite(totalBudget) || totalBudget < 0) {
      setActionError('Gesamtbudget muss eine gültige positive Zahl sein.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        year: planForm.year,
        title: planForm.title,
        description: planForm.description,
        description_md: planForm.description,
        status: planForm.status,
        goals,
        total_budget: totalBudget,
      };
      if (editingPlan) {
        await api.patch<ApiResponse<MarketingPlan>>(`/marketing-plans/${editingPlan.id}`, payload);
      } else {
        await api.post<ApiResponse<MarketingPlan>>('/marketing-plans', payload);
      }
      setPlanDialogOpen(false);
      if (planForm.year !== year) setYear(planForm.year);
      else await loadPlan();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Plan konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivatePlan = async () => {
    if (!plan) return;
    setActionError(null);
    const confirmed = await confirmDialog({
      title: 'Marketingplan deaktivieren?',
      description: 'Der Plan wird deaktiviert und ist nicht mehr aktiv sichtbar.',
      confirmText: 'Deaktivieren',
      cancelText: 'Abbrechen',
      variant: 'destructive',
    });
    if (!confirmed) return;
    setSaving(true);
    try {
      await api.patch<ApiResponse>(`/marketing-plans/${plan.id}/deactivate`);
      setPlan(null);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Plan konnte nicht deaktiviert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleClearPlan = async () => {
    if (!plan) return;
    setActionError(null);
    const confirmed = await confirmDialog({
      title: 'Planinhalt leeren?',
      description: 'Beschreibung, Ziele und Budget werden entfernt. Titel und Jahr bleiben erhalten.',
      confirmText: 'Inhalt leeren',
      cancelText: 'Abbrechen',
      variant: 'destructive',
    });
    if (!confirmed) return;
    const snapshot = {
      id: plan.id,
      title: plan.title,
      description: plan.description ?? plan.description_md ?? '',
      status: plan.status,
      goals: plan.goals ?? [],
      total_budget: plan.total_budget ?? 0,
      year: plan.year,
    };
    setSaving(true);
    try {
      await api.patch<ApiResponse<MarketingPlan>>(`/marketing-plans/${plan.id}`, {
        year: plan.year,
        title: plan.title,
        description: '',
        description_md: '',
        status: 'draft',
        goals: [],
        total_budget: 0,
      });
      setLastPlanSnapshot(snapshot);
      await loadPlan();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Plan konnte nicht geleert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleUndoClear = async () => {
    if (!plan || !lastPlanSnapshot || lastPlanSnapshot.id !== plan.id) return;
    setActionError(null);
    setSaving(true);
    try {
      await api.patch<ApiResponse<MarketingPlan>>(`/marketing-plans/${plan.id}`, {
        year: lastPlanSnapshot.year,
        title: lastPlanSnapshot.title,
        description: lastPlanSnapshot.description,
        description_md: lastPlanSnapshot.description,
        status: lastPlanSnapshot.status,
        goals: lastPlanSnapshot.goals,
        total_budget: lastPlanSnapshot.total_budget,
      });
      setLastPlanSnapshot(null);
      await loadPlan();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Rückgängig konnte nicht ausgeführt werden.');
    } finally {
      setSaving(false);
    }
  };

  const openPhaseDialog = (phase?: MarketingPhase) => {
    setActionError(null);
    if (phase) {
      setEditingPhase(phase);
      setPhaseForm({
        name: phase.name,
        description: phase.description ?? '',
        status: phase.status,
        start_date: phase.start_date ?? '',
        end_date: phase.end_date ?? '',
      });
    } else {
      setEditingPhase(null);
      setPhaseForm({
        name: '',
        description: '',
        status: 'pending',
        start_date: '',
        end_date: '',
      });
    }
    setPhaseDialogOpen(true);
  };

  const savePhase = async () => {
    if (!plan) return;
    setActionError(null);
    if (!phaseForm.name.trim()) {
      setActionError('Bitte einen Phasennamen eingeben.');
      return;
    }
    if (phaseForm.start_date && phaseForm.end_date && phaseForm.end_date < phaseForm.start_date) {
      setActionError('Das Enddatum darf nicht vor dem Startdatum liegen.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...phaseForm, start_date: phaseForm.start_date || null, end_date: phaseForm.end_date || null };
      if (editingPhase) {
        await api.patch<ApiResponse<MarketingPhase>>(
          `/marketing-phases/${editingPhase.id}`,
          payload
        );
      } else {
        await api.post<ApiResponse<MarketingPhase>>('/marketing-phases', {
          ...payload,
          plan_id: plan.id,
          sort_order: (plan.phases?.length ?? 0),
        });
      }
      setPhaseDialogOpen(false);
      await loadPlan();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Phase konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const postPlanComment = async () => {
    if (!plan || !newComment.trim() || postingComment) return;
    setActionError(null);
    setPostingComment(true);
    try {
      await api.post(`/marketing-plans/${plan.id}/comments`, { content: newComment.trim() });
      setNewComment('');
      await loadPlan();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Kommentar konnte nicht gespeichert werden.');
    } finally {
      setPostingComment(false);
    }
  };

  const openMeasureDialog = (goalId: string) => {
    setActionError(null);
    setMeasureGoalId(goalId);
    setMeasureForm({ period: 'Q1', value: '', notes: '' });
    setMeasureDialogOpen(true);
  };

  const saveMeasurement = async () => {
    if (!plan || !measureGoalId) return;
    const value = parseFloat(measureForm.value);
    if (!Number.isFinite(value)) {
      setActionError('Bitte einen gültigen Zahlenwert eingeben.');
      return;
    }
    setSavingMeasure(true);
    setActionError(null);
    try {
      const newMeasurement: PlanGoalMeasurement = {
        period: measureForm.period,
        value,
        date: new Date().toISOString().substring(0, 10),
        ...(measureForm.notes.trim() ? { notes: measureForm.notes.trim() } : {}),
      };
      const updatedGoals = plan.goals.map((g) => {
        if (g.id !== measureGoalId) return g;
        const existing = g.measurements ?? [];
        const idx = existing.findIndex((m) => m.period === newMeasurement.period);
        const measurements =
          idx >= 0
            ? existing.map((m, i) => (i === idx ? newMeasurement : m))
            : [...existing, newMeasurement];
        return { ...g, measurements };
      });
      await api.patch<ApiResponse<MarketingPlan>>(`/marketing-plans/${plan.id}`, {
        goals: updatedGoals,
      });
      setPlan((p) => (p ? { ...p, goals: updatedGoals } : p));
      setMeasureDialogOpen(false);
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : 'Messung konnte nicht gespeichert werden.'
      );
    } finally {
      setSavingMeasure(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);
  const exportPlan = () => {
    if (!plan) return;
    const url = URL.createObjectURL(new Blob([JSON.stringify(plan, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `marketing-plan-${plan.year}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const spent = plan?.phases?.reduce(
    (sum, p) =>
      sum +
      (p.activities?.reduce((a, b) => a + b.budget_actual, 0) ?? 0),
    0
  ) ?? 0;
  const totalBudget = plan?.total_budget ?? 0;
  const budgetPercent = totalBudget > 0 ? Math.min(100, (spent / totalBudget) * 100) : 0;

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="h-10 w-64 animate-pulse rounded-md bg-card-hover" />
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-xl border border-border bg-card-hover dark:bg-card"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      {actionError && (
        <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          {actionError}
        </div>
      )}
      {/* Header */}
      <div className="space-y-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <div className="inline-flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted">Jahr</span>
                    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                      <SelectTrigger className="w-[120px]" aria-label="Planjahr">
                        <SelectValue placeholder="Jahr" />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">
                  Welcher Jahresplan angezeigt wird. Jeder Plan gehört zu genau einem Jahr — wechseln Sie hier, um andere
                  Jahrespläne zu öffnen.
                </TooltipContent>
              </Tooltip>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                {plan?.title ?? `Marketing-Plan ${year}`}
              </h1>
              {plan && (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Badge className={cn(statusColors[plan.status])}>
                        {PLAN_STATUS_LABELS[plan.status]}
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-sm">
                    Entwurf = in Arbeit; Aktiv = offizieller Plan; Abgeschlossen/Archiviert = abgeschlossene Perioden.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button variant="outline" size="sm" onClick={() => openPlanDialog(plan ?? undefined)}>
                    <Pencil className="h-4 w-4" />
                    Bearbeiten
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-sm">
                Titel, Jahr, Gesamtbudget, Status, Beschreibung und Ziele (JSON) bearbeiten.
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button variant="outline" size="sm" onClick={exportPlan} disabled={!plan}>
                    <Download className="h-4 w-4" />
                    JSON exportieren
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-sm">
                Plan mit Phasen und Aktivitäten als JSON herunterladen.
              </TooltipContent>
            </Tooltip>
            {plan && (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button size="sm" onClick={handleCopyPlan} disabled={copying}>
                      <Copy className="h-4 w-4" />
                      Plan kopieren
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">
                  Erstellt eine Kopie des Plans (z. B. für ein Folgejahr oder eine Variante) — je nach Konfiguration mit
                  Phasen und Aktivitäten.
                </TooltipContent>
              </Tooltip>
            )}
            {plan && (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button variant="outline" size="sm" onClick={handleClearPlan} disabled={saving}>
                      <Eraser className="h-4 w-4" />
                      Plan leeren
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">
                  Entfernt Inhalte aus dem Plan — meist mit Sicherheitsabfrage. Für Wiederherstellung siehe «Rückgängig».
                </TooltipContent>
              </Tooltip>
            )}
            {plan && lastPlanSnapshot?.id === plan.id && (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button variant="outline" size="sm" onClick={handleUndoClear} disabled={saving}>
                      <Undo2 className="h-4 w-4" />
                      Rückgängig
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">
                  Stellt den Stand vor dem letzten Leeren wieder her, solange ein Snapshot vorliegt.
                </TooltipContent>
              </Tooltip>
            )}
            {plan && (
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button variant="destructive" size="sm" onClick={handleDeactivatePlan} disabled={saving}>
                      <Trash2 className="h-4 w-4" />
                      Deaktivieren
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">
                  Archiviert oder deaktiviert den Plan — Auswirkungen auf Sichtbarkeit je nach Rolle. Nur mit Bedacht
                  nutzen.
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {!plan ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ImageIcon className="mb-3 h-8 w-8 text-muted" />
            <p className="mb-2 max-w-md text-center text-muted">
              Kein Marketing-Plan für {year} vorhanden. Legen Sie einen Plan an — mit Budget, Phasen und Aktivitäten wird
              die operative Umsetzung steuerbar. Die Marketingstrategie (separater Bereich) ergänzt die Richtung pro Jahr.
            </p>
            <Button onClick={() => openPlanDialog()}>
              <Plus className="h-4 w-4" />
              Plan erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Strategy Banner */}
          <StrategyBanner plan={plan} onLinked={loadPlan} />

          {/* Description */}
          {(plan.description ?? plan.description_md) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center gap-2">
                  <Lightbulb className="h-5 w-5 shrink-0" />
                  Beschreibung
                  <InfoTooltip text="Aus dem Plan-Dialog: strategischer Kontext, Zielgruppen, Prioritäten — oft Markdown. Ergänzt die Marketingstrategie um operative Details." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative overflow-hidden rounded-lg border border-border p-4">
                  <div
                    className="pointer-events-none absolute inset-0 opacity-[0.12] dark:opacity-[0.08]"
                    style={{
                      backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.98), rgba(255,255,255,0.82)), url(${MARKETING_PLAN_STOCK_IMAGE})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                  <div className="prose prose-slate dark:prose-invert relative max-w-none whitespace-pre-wrap text-sm">
                    {plan.description ?? plan.description_md}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Goals */}
          {plan.goals && plan.goals.length > 0 && (
            <section>
              <SectionHeadingWithHint
                className="!mb-4"
                icon={<Target className="h-5 w-5 shrink-0 text-foreground dark:text-muted" />}
                title="Ziele"
                hint="Messbare Jahresziele mit optionalen Quartalswerten. Erfassen Sie Ist-Messungen, damit der Fortschrittsbalken und Reports stimmen."
              />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {plan.goals.map((goal: PlanGoal) => {
                  const targetNum = parseFloat(goal.target);
                  const hasNumericTarget = !isNaN(targetNum) && targetNum > 0;
                  const measurements = goal.measurements ?? [];
                  const latestMeasurement =
                    measurements.length > 0
                      ? measurements.reduce((a, b) => (a.date >= b.date ? a : b))
                      : null;
                  const progressPct =
                    hasNumericTarget && latestMeasurement !== null
                      ? Math.min(100, (latestMeasurement.value / targetNum) * 100)
                      : null;
                  const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;
                  return (
                    <Card key={goal.id}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-base">{goal.title}</CardTitle>
                          {progressPct !== null && (
                            <span
                              className={cn(
                                'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold',
                                progressPct >= 100
                                  ? 'bg-success-soft text-success'
                                  : progressPct >= 70
                                    ? 'bg-warning-soft text-warning'
                                    : 'bg-danger-soft text-danger'
                              )}
                            >
                              {Math.round(progressPct)} %
                            </span>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {hasNumericTarget ? (
                          <>
                            <div className="space-y-1">
                              <div className="flex justify-between text-xs text-muted">
                                <span>
                                  {latestMeasurement ? latestMeasurement.value : 0}{' '}
                                  {goal.metric}
                                </span>
                                <span>
                                  Ziel: {goal.target} {goal.metric}
                                </span>
                              </div>
                              <Progress value={progressPct ?? 0} className="h-2" />
                            </div>
                            <div className="grid grid-cols-4 gap-1">
                              {QUARTERS.map((q) => {
                                const m = measurements.find((x) => x.period === q);
                                return (
                                  <div
                                    key={q}
                                    className="rounded border border-border bg-background p-1.5 text-center dark:bg-card-hover/60"
                                  >
                                    <p className="text-[10px] font-semibold text-muted">
                                      {q}
                                    </p>
                                    <p
                                      className={cn(
                                        'text-xs font-medium',
                                        m
                                          ? 'text-muted dark:text-foreground'
                                          : 'text-muted'
                                      )}
                                    >
                                      {m ? m.value : '–'}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-muted">
                            {[goal.target, goal.metric].filter(Boolean).join(' ') ||
                              'Zielbeschreibung ohne Metrik'}
                          </p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => openMeasureDialog(goal.id)}
                        >
                          <BarChart2 className="mr-1 h-3.5 w-3.5" />
                          Messung erfassen
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {/* Phases timeline */}
          {plan.phases && plan.phases.length > 0 && (
            <section>
              <SectionHeadingWithHint
                className="!mb-4"
                icon={<Calendar className="h-5 w-5 shrink-0 text-foreground dark:text-muted" />}
                title="Phasen"
                hint="Zeitliche Blöcke (z. B. Q1–Q4): darunter gruppieren Sie Aktivitäten. Die Timeline zeigt Status und Fortschritt — Klick auf einen Punkt fokussiert die Phase."
                actions={
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Button variant="outline" size="sm" onClick={() => openPhaseDialog()}>
                          <Plus className="h-4 w-4" />
                          Phase hinzufügen
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-sm">
                      Neue Phase mit Zeitraum und Status anlegen — danach Aktivitäten zuordnen.
                    </TooltipContent>
                  </Tooltip>
                }
              />
              <div className="relative mb-6 w-full overflow-hidden rounded-xl border border-border/90 bg-gradient-to-b from-background/90 to-white px-2 py-3 shadow-sm dark:border-border dark:from-card/80 dark:to-background/90 sm:px-3">
                {(() => {
                  const sortedPhases = plan.phases
                    .slice()
                    .sort((a, b) => a.sort_order - b.sort_order);
                  const progress = getPhaseTimelineProgressMeta(sortedPhases);
                  return (
                    <div className="relative w-full">
                      {/* Track + Füllung: exakt in der gleichen Höhe wie die Punkt-Zeile (h-11), zentriert mit top-1/2 */}
                      <div
                        className="pointer-events-none absolute inset-x-0 top-0 z-0 h-11"
                        aria-hidden
                      >
                        <div className="absolute inset-x-0 top-1/2 z-0 h-[3px] -translate-y-1/2 rounded-full bg-card-hover dark:bg-muted" />
                        {progress.widthPct > 0 && (
                          <div
                            className={cn(
                              'absolute left-0 top-1/2 z-[1] h-[3px] max-w-full -translate-y-1/2 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.35)] transition-[width] duration-700 ease-out dark:shadow-[0_0_12px_rgba(52,211,153,0.25)]',
                              progress.endStatus === 'in_progress'
                                ? 'bg-gradient-to-r from-success via-success to-warning '
                                : 'bg-success',
                            )}
                            style={{ width: `${progress.widthPct}%` }}
                          />
                        )}
                      </div>
                      <div className="relative z-10 flex w-full min-w-0">
                        {sortedPhases.map((phase) => (
                          <div
                            key={phase.id}
                            className="flex min-w-0 flex-1 flex-col items-stretch px-0.5 sm:px-1"
                          >
                            <button
                              type="button"
                              onClick={() => focusPhaseFromTimeline(phase.id)}
                              title={phase.name}
                              aria-label={`Phase: ${phase.name}`}
                              className="group flex w-full flex-col items-center rounded-lg px-0.5 pb-1 pt-0 text-left transition-colors hover:bg-card-hover/80 dark:hover:bg-card-hover/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-border focus-visible:ring-offset-2 dark:focus-visible:ring-offset-background"
                            >
                              <span className="relative flex h-11 w-full shrink-0 items-center justify-center">
                                {phase.status === 'in_progress' && (
                                  <span
                                    className="absolute inline-flex h-9 w-9 animate-ping rounded-full bg-warning/30 motion-reduce:animate-none"
                                    aria-hidden
                                  />
                                )}
                                <span
                                  className={cn(
                                    'relative z-10 shrink-0 rounded-full border-2 border-white shadow-sm dark:border-border',
                                    phase.status === 'completed' &&
                                      'h-3.5 w-3.5 bg-success ring-2 ring-success/30',
                                    phase.status === 'in_progress' &&
                                      'h-4 w-4 bg-warning ring-2 ring-warning/30',
                                    phase.status === 'pending' &&
                                      'h-3.5 w-3.5 border-[2.5px] border-border bg-card shadow-none',
                                  )}
                                />
                              </span>
                              <span className="line-clamp-2 w-full px-0.5 pt-2 text-center text-[11px] font-medium leading-snug text-muted hyphens-auto dark:text-foreground sm:text-xs">
                                {phase.name}
                              </span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="space-y-2">
                {plan.phases
                  .slice()
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((phase) => {
                    const isExpanded = expandedPhases.has(phase.id);
                    const activityCount = phase.activities?.length ?? 0;
                    return (
                      <Card key={phase.id} id={`marketing-phase-${phase.id}`} className="scroll-mt-4">
                        <div className="flex w-full items-center justify-between gap-2 p-4">
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 items-center gap-3 text-left"
                            onClick={() => togglePhase(phase.id)}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted" />
                            ) : (
                              <ChevronRight className="h-4 w-4 shrink-0 text-muted" />
                            )}
                            <span className="font-medium">{phase.name}</span>
                            <Badge className={cn(statusColors[phase.status])}>
                              {PHASE_STATUS_LABELS[phase.status]}
                            </Badge>
                            <span className="text-sm text-muted">
                              {activityCount} Aktivität{activityCount !== 1 ? 'en' : ''}
                            </span>
                            {phase.start_date && (
                              <span className="text-sm text-muted">
                                {formatDate(phase.start_date)} –{' '}
                                {phase.end_date ? formatDate(phase.end_date) : '–'}
                              </span>
                            )}
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="shrink-0"
                            onClick={() => openPhaseDialog(phase)}
                          >
                            <Pencil className="h-4 w-4" />
                            Bearbeiten
                          </Button>
                        </div>
                        {isExpanded && (
                          <CardContent className="border-t border-border pt-4">
                            {phase.description && (
                              <p className="mb-4 text-sm text-muted">
                                {phase.description}
                              </p>
                            )}
                            {activityCount === 0 ? (
                              <p className="text-sm text-muted">
                                Keine Aktivitäten in dieser Phase.
                              </p>
                            ) : (
                              <div className="space-y-0">
                                <PhaseGanttView
                                  phase={phase}
                                  onActivityClick={(id) => setSelectedActivityId(id)}
                                />
                              </div>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
              </div>
            </section>
          )}

          {/* Budget summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet2 className="h-5 w-5" />
                Budget-Übersicht
              </CardTitle>
              <CardDescription>
                {formatCurrency(spent)} von {formatCurrency(totalBudget)} ausgegeben
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Progress value={budgetPercent} className="h-3" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Kommentare zum Jahresplan
              </CardTitle>
              <CardDescription>
                Zusammenarbeit und Hinweise direkt am Plan dokumentieren.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {(plan.comments ?? []).length === 0 ? (
                  <p className="text-sm text-muted">Noch keine Kommentare vorhanden.</p>
                ) : (
                  (plan.comments ?? []).map((comment) => (
                    <div key={comment.id} className="rounded-lg border border-border p-3">
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-foreground">
                          {comment.user?.full_name ?? 'Unbekannt'}
                        </p>
                        <p className="text-xs text-muted">{formatDate(comment.created_at)}</p>
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-muted">{comment.content}</p>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-2">
                <AITextarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onValueChange={setNewComment}
                  rows={3}
                  placeholder="Kommentar zum Plan hinzufügen…"
                  aiFieldLabel="Plan-Kommentar"
                  aiContext={`Kommentar zum Marketing-Plan: ${plan?.title ?? ''}`}
                />
                <Button onClick={postPlanComment} disabled={!newComment.trim() || postingComment}>
                  {postingComment ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Kommentar hinzufügen
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Activity detail drawer */}
      <ActivityDetailDrawer
        activityId={selectedActivityId}
        open={selectedActivityId !== null}
        onClose={() => setSelectedActivityId(null)}
        onUpdated={loadPlan}
      />

      {/* Plan dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="flex max-h-[min(90vh,920px)] w-[min(100vw-2rem,42rem)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
          <div className="shrink-0 px-6 pb-2 pt-6 pr-14">
            <DialogHeader>
              <DialogTitle>
                {editingPlan ? 'Plan bearbeiten' : 'Plan erstellen'}
              </DialogTitle>
              <DialogDescription>
                Titel und Beschreibung des Marketing-Plans.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-6 [scrollbar-gutter:stable]">
            {actionError && <p role="alert" className="mb-3 text-sm text-danger">{actionError}</p>}
            <div className="grid gap-4 pb-2">
            <div className="grid gap-2">
              <Label>Jahr</Label>
              <Input
                type="number"
                min={2000}
                max={2100}
                value={planForm.year}
                onChange={(e) =>
                  setPlanForm((p) => ({ ...p, year: Number(e.target.value) || CURRENT_YEAR }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label>Titel</Label>
              <AIInput
                value={planForm.title}
                onChange={(e) => setPlanForm((p) => ({ ...p, title: e.target.value }))}
                onValueChange={(v) => setPlanForm((p) => ({ ...p, title: v }))}
                placeholder="Marketing-Plan 2026"
                aiFieldLabel="Marketing-Plan Titel"
                aiContext={`Jahres-Marketing-Plan ${planForm.year}`}
              />
            </div>
            <div className="grid gap-2">
              <Label>Beschreibung</Label>
              <AITextarea
                value={planForm.description}
                onChange={(e) => setPlanForm((p) => ({ ...p, description: e.target.value }))}
                onValueChange={(v) => setPlanForm((p) => ({ ...p, description: v }))}
                rows={4}
                placeholder="Beschreibung des Plans…"
                aiFieldLabel="Marketing-Plan Beschreibung"
                aiContext={`Jahres-Marketing-Plan ${planForm.year}, Titel: ${planForm.title}`}
              />
            </div>
            <div className="grid gap-2">
              <Label>Gesamtbudget</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={planForm.total_budget}
                onChange={(e) =>
                  setPlanForm((p) => ({ ...p, total_budget: e.target.value }))
                }
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label>Ziele</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPlanForm((p) => ({
                      ...p,
                      goals: [
                        ...p.goals,
                        { id: `goal-${Date.now()}`, title: '', target: '', metric: '' },
                      ],
                    }))
                  }
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Ziel hinzufügen
                </Button>
              </div>
              {planForm.goals.length === 0 && (
                <p className="text-sm text-muted-foreground py-2">
                  Noch keine Ziele definiert.
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2 sm:gap-3">
                {planForm.goals.map((goal, idx) => (
                  <div key={goal.id} className="relative grid gap-2 rounded-md border p-3 sm:min-w-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() =>
                        setPlanForm((p) => ({
                          ...p,
                          goals: p.goals.filter((_, i) => i !== idx),
                        }))
                      }
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                    <div className="grid gap-1">
                      <Label className="text-xs text-muted-foreground">Titel</Label>
                      <Input
                        value={goal.title}
                        onChange={(e) =>
                          setPlanForm((p) => ({
                            ...p,
                            goals: p.goals.map((g, i) =>
                              i === idx ? { ...g, title: e.target.value } : g
                            ),
                          }))
                        }
                        placeholder="z. B. 200 neue Leads generieren"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">Zielwert</Label>
                        <Input
                          value={goal.target ?? ''}
                          onChange={(e) =>
                            setPlanForm((p) => ({
                              ...p,
                              goals: p.goals.map((g, i) =>
                                i === idx ? { ...g, target: e.target.value } : g
                              ),
                            }))
                          }
                          placeholder="z. B. 200"
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">Metrik</Label>
                        <Input
                          value={goal.metric ?? ''}
                          onChange={(e) =>
                            setPlanForm((p) => ({
                              ...p,
                              goals: p.goals.map((g, i) =>
                                i === idx ? { ...g, metric: e.target.value } : g
                              ),
                            }))
                          }
                          placeholder="z. B. Leads"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={planForm.status}
                onValueChange={(v) =>
                  setPlanForm((p) => ({ ...p, status: v as PlanStatus }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(PLAN_STATUS_LABELS) as PlanStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {PLAN_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          </div>
          <div className="shrink-0 border-t border-border bg-background/90 px-6 py-4 dark:bg-card/50">
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>
                <X className="h-4 w-4" />
                Abbrechen
              </Button>
              <Button onClick={savePlan} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Speichern…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Speichern
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Measurement dialog */}
      <Dialog open={measureDialogOpen} onOpenChange={setMeasureDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Messung erfassen</DialogTitle>
            <DialogDescription>
              Trage den aktuellen Ist-Wert für das gewählte Quartal ein.
            </DialogDescription>
          </DialogHeader>
          {actionError && <p role="alert" className="text-sm text-danger">{actionError}</p>}
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Periode</Label>
              <Select
                value={measureForm.period}
                onValueChange={(v) =>
                  setMeasureForm((p) => ({ ...p, period: v as PlanGoalMeasurement['period'] }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1">Q1 (Jan – Mär)</SelectItem>
                  <SelectItem value="Q2">Q2 (Apr – Jun)</SelectItem>
                  <SelectItem value="Q3">Q3 (Jul – Sep)</SelectItem>
                  <SelectItem value="Q4">Q4 (Okt – Dez)</SelectItem>
                  <SelectItem value="year">Gesamt Jahr</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>
                Aktueller Wert
                {measureGoalId &&
                  (() => {
                    const g = plan?.goals.find((x) => x.id === measureGoalId);
                    return g?.metric ? ` (${g.metric})` : '';
                  })()}
              </Label>
              <Input
                type="number"
                value={measureForm.value}
                onChange={(e) => setMeasureForm((p) => ({ ...p, value: e.target.value }))}
                placeholder="z. B. 145"
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label>Notiz (optional)</Label>
              <Input
                value={measureForm.notes}
                onChange={(e) => setMeasureForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="z. B. Quelle: CRM-Export"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeasureDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button
              onClick={saveMeasurement}
              disabled={savingMeasure || !measureForm.value.trim()}
            >
              {savingMeasure ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase dialog */}
      <Dialog open={phaseDialogOpen} onOpenChange={setPhaseDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPhase ? 'Phase bearbeiten' : 'Phase hinzufügen'}
            </DialogTitle>
            <DialogDescription>
              Name, Zeitraum und Status der Phase.
            </DialogDescription>
          </DialogHeader>
          {actionError && <p role="alert" className="text-sm text-danger">{actionError}</p>}
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name</Label>
              <AIInput
                value={phaseForm.name}
                onChange={(e) => setPhaseForm((p) => ({ ...p, name: e.target.value }))}
                onValueChange={(v) => setPhaseForm((p) => ({ ...p, name: v }))}
                placeholder="z. B. Q1 Kampagnen"
                aiFieldLabel="Marketing-Phase Name"
                aiContext={`Phase im Marketing-Plan ${plan?.title ?? ''}, Zeitraum: ${phaseForm.start_date} – ${phaseForm.end_date}`}
              />
            </div>
            <div className="grid gap-2">
              <Label>Beschreibung</Label>
              <AITextarea
                value={phaseForm.description}
                onChange={(e) => setPhaseForm((p) => ({ ...p, description: e.target.value }))}
                onValueChange={(v) => setPhaseForm((p) => ({ ...p, description: v }))}
                rows={2}
                aiFieldLabel="Marketing-Phase Beschreibung"
                aiContext={`Phase: ${phaseForm.name || 'neue Phase'} im Marketing-Plan ${plan?.title ?? ''}`}
              />
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={phaseForm.status}
                onValueChange={(v) =>
                  setPhaseForm((p) => ({ ...p, status: v as PhaseStatus }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(PHASE_STATUS_LABELS) as PhaseStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {PHASE_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Startdatum</Label>
                <Input
                  type="date"
                  value={phaseForm.start_date}
                  onChange={(e) =>
                    setPhaseForm((p) => ({ ...p, start_date: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Enddatum</Label>
                <Input
                  type="date"
                  value={phaseForm.end_date}
                  onChange={(e) =>
                    setPhaseForm((p) => ({ ...p, end_date: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhaseDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button onClick={savePhase} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Speichern…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── StrategyBanner ────────────────────────────────────────────────────────────
const STRATEGY_STATUS_LABEL: Record<string, string> = {
  draft: 'Entwurf',
  active: 'Aktiv',
  archived: 'Archiv',
};

function StrategyBanner({
  plan,
  onLinked,
}: {
  plan: MarketingPlanType;
  onLinked: () => Promise<void>;
}) {
  const { openPanel, loadStrategyForYear } = useStrategyStore();
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [strategies, setStrategies] = useState<MarketingStrategy[]>([]);
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedStrategyId, setSelectedStrategyId] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!linkDialogOpen) return;
    setListError(null);
    setLinkError(null);
    setSelectedStrategyId('');
    setLoadingStrategies(true);
    const q = new URLSearchParams({ year: String(plan.year), limit: '100', page: '1' });
    void api
      .get<PaginatedResponse<MarketingStrategy>>(`/marketing-strategies?${q.toString()}`)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setStrategies(list);
        const first = list[0];
        if (first) setSelectedStrategyId(first.id);
      })
      .catch((err) => {
        setStrategies([]);
        setListError(err instanceof Error ? err.message : 'Strategien konnten nicht geladen werden.');
      })
      .finally(() => setLoadingStrategies(false));
  }, [linkDialogOpen, plan.year]);

  const selectedStrategy = strategies.find((s) => s.id === selectedStrategyId);
  const replacesOtherPlanLink =
    selectedStrategy?.plan_id != null && selectedStrategy.plan_id !== plan.id;
  const replacedPlanTitle = (() => {
    if (!replacesOtherPlanLink || !selectedStrategy) return null;
    const mp = selectedStrategy.marketing_plans;
    if (!mp) return null;
    return Array.isArray(mp) ? mp[0]?.title : mp.title;
  })();

  const handleLinkExisting = async () => {
    if (!selectedStrategyId) return;
    setLinking(true);
    setLinkError(null);
    try {
      await api.patch<ApiResponse<MarketingStrategy>>(`/marketing-strategies/${selectedStrategyId}`, {
        plan_id: plan.id,
      });
      setLinkDialogOpen(false);
      await onLinked();
      await loadStrategyForYear(plan.year);
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Verknüpfung ist fehlgeschlagen.');
    } finally {
      setLinking(false);
    }
  };

  if (plan.strategy_id) {
    return (
      <div className="mb-4 flex flex-col gap-3 rounded-lg border border-border bg-card-hover px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:bg-primary-hover/20">
        <div className="flex min-w-0 items-center gap-2 text-sm text-foreground dark:text-muted">
          <Target className="h-4 w-4 shrink-0" />
          <span className="font-medium">Marketingstrategie {plan.year} verknüpft</span>
          <span className="hidden text-muted sm:inline">– Ziele und Kanäle für das Jahr festgelegt</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void openPanel(plan.year)}
          className="w-full shrink-0 border-border text-foreground hover:bg-card-hover sm:w-auto dark:text-muted"
        >
          <Target className="h-3.5 w-3.5" />
          Strategie anzeigen
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 rounded-lg border border-dashed border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="min-w-0 flex-1 text-sm text-muted">
          Noch keine Marketingstrategie für {plan.year} verknüpft.
        </p>
        <div className="flex w-full flex-wrap items-stretch justify-end gap-2 sm:w-auto sm:items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLinkDialogOpen(true)}
            className="min-w-0 flex-1 sm:flex-initial"
          >
            <Link2 className="h-3.5 w-3.5 shrink-0" />
            Bestehende verknüpfen
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void openPanel(plan.year)}
            className="min-w-0 flex-1 text-foreground sm:flex-initial dark:text-muted"
          >
            <Target className="h-3.5 w-3.5 shrink-0" />
            Jetzt erstellen
          </Button>
        </div>
      </div>

      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Marketingstrategie verknüpfen</DialogTitle>
            <DialogDescription>
              Wählen Sie eine Strategie für {plan.year}. Dieser Plan wird als verknüpfter Marketingplan gespeichert.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {loadingStrategies ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted">
                <Loader2 className="h-5 w-5 animate-spin" />
                Strategien werden geladen…
              </div>
            ) : listError ? (
              <p className="text-sm text-danger">{listError}</p>
            ) : strategies.length === 0 ? (
              <div className="space-y-3 text-sm text-muted">
                <p>Keine Strategie für {plan.year} vorhanden. Legen Sie eine unter «Marketingstrategie» an oder nutzen Sie «Jetzt erstellen».</p>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    window.location.hash = '#/marketing-strategy';
                    setLinkDialogOpen(false);
                  }}
                >
                  Zur Marketingstrategie
                </Button>
              </div>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="strategy-link-select">Strategie</Label>
                  <Select value={selectedStrategyId} onValueChange={setSelectedStrategyId}>
                    <SelectTrigger id="strategy-link-select" className="w-full">
                      <SelectValue placeholder="Strategie wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {strategies.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span className="truncate">
                            {s.title}
                            {STRATEGY_STATUS_LABEL[s.status] ? ` (${STRATEGY_STATUS_LABEL[s.status]})` : ''}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {replacesOtherPlanLink && (
                  <p className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
                    Diese Strategie ist aktuell mit einem anderen Marketingplan verknüpft
                    {replacedPlanTitle ? ` („${replacedPlanTitle}“)` : ''}. Beim Verknüpfen wird die bisherige Zuordnung
                    aufgehoben.
                  </p>
                )}
                {linkError && (
                  <p className="text-sm text-danger">{linkError}</p>
                )}
              </>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setLinkDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={() => void handleLinkExisting()}
              disabled={linking || loadingStrategies || !!listError || strategies.length === 0 || !selectedStrategyId}
            >
              {linking ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verknüpfen…
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4" />
                  Verknüpfen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
